import React from "react";
import { formatBRL } from "../../lib/api";
import { Loader2, AlertTriangle } from "lucide-react";
import { maskCPF } from "../../lib/representatives";
import { advancePercentFromValue, formatAdvancePercent } from "../../lib/operations";
import { backingTypeLabel } from "../../lib/backingTypes";
import { parseDecimalBR } from "../../lib/numberFormat";

// R5 — extraído MECANICAMENTE de pages/NewOperation.jsx (JSX verbatim; o
// estado vive no componente-pai e chega por `ctx`).
export default function StepRevisao({ ctx }) {
  const {
    cfg,
    isCedenteMode,
    simulating,
    simulationResult,
    simulationError,
    terminalError,
    taxa,
    descricao,
    product,
    cedente,
    taxaNum,
    cedenteIsPJ,
    selectedSigners,
    validReceivables,
    totalReceivables,
    prazoDias,
    runSimulation,
    policyRejected,
    allowedRangeText,
    policyRejectText,
    oprLiquidValue,
    oprDiscountValue,
    liquidoVisivel,
  } = ctx;
  return (
    <div className="zemo-card p-6 space-y-5" data-testid="step-4-review">
      <h2 className="text-lg font-semibold tracking-tight">Revisao da operacao</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          ["Produto", product?.nome],
          ["Tipo", product?.tipo],
          ["Cedente", cedente?.razao_social],
          ["CNPJ", cedente?.cnpj],
          ["Total a antecipar", formatBRL(totalReceivables)],
          // Modo reduzido (01(b)): a linha "Taxa" some para o cedente.
          ...(cfg.showRates ? [["Taxa", taxaNum > 0 ? `${taxa}% a.m.` : "Policy V2"]] : []),
          ["Prazo (maior vencimento)", `${prazoDias} dias`],
          [isCedenteMode ? "Valor liquido estimado" : "Valor liquido (simulacao V2)",
            (isCedenteMode ? liquidoVisivel : oprLiquidValue != null)
              ? formatBRL(parseFloat(oprLiquidValue))
              : "--"],
          ["Assinantes do contrato",
            cedenteIsPJ
              ? `${selectedSigners.length} representante(s)`
              : "Cedente PF (assina ele mesmo)"],
        ].map(([k, v]) => (
          <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/40">
            <div className="zemo-overline">{k}</div>
            <div className="mt-1 font-semibold text-zinc-900 capitalize">{v || "--"}</div>
          </div>
        ))}
      </div>

      {descricao && (
        <div className="p-3 border border-[var(--zemo-border)] rounded-md">
          <div className="zemo-overline">Descricao</div>
          <div className="mt-1 text-sm text-zinc-700">{descricao}</div>
        </div>
      )}

      {/* Receivables summary */}
      {validReceivables.length > 0 && (
        <div className="space-y-2">
          <div className="zemo-overline">Recebiveis ({validReceivables.length})</div>
          {/* UI-4: `overflow-x-auto` (padrão das outras 9 tabelas do
              Portal) e não `overflow-hidden` — com 7 colunas, três delas
              `font-mono-num` (que não quebram linha), o excesso era
              CORTADO em viewport estreito e o usuário perdia a última
              coluna sem barra de rolagem que dissesse que ela existe. */}
          <div className="border border-[var(--zemo-border)] rounded-md overflow-x-auto" data-testid="review-receivables-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-[var(--zemo-border)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-zinc-600">NF</th>
                  <th className="text-left px-3 py-2 font-medium text-zinc-600">Sacado</th>
                  <th className="text-left px-3 py-2 font-medium text-zinc-600">Lastro</th>
                  {/* Mesmo campo (`net_face_value`) do passo de recebíveis:
                      o cabeçalho segue o rótulo do modo (item 09.2). */}
                  <th className="text-right px-3 py-2 font-medium text-zinc-600">{cfg.copy.valorNotaLabelCurto}</th>
                  {/* Q8 — o percentual DERIVADO do valor, pelo mesmo
                      cálculo do campo. É a última tela antes do envio: quem
                      antecipa parte da nota confere aqui a proporção.
                      UI-5: na MESMA ordem do formulário (base → percentual →
                      resultado); a mesma tríade lida em duas ordens
                      diferentes no mesmo wizard cobra releitura. */}
                  <th className="text-right px-3 py-2 font-medium text-zinc-600">{cfg.copy.percentualLabelCurto}</th>
                  <th className="text-right px-3 py-2 font-medium text-zinc-600">A antecipar</th>
                  <th className="text-right px-3 py-2 font-medium text-zinc-600">Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {validReceivables.map((r, i) => (
                  <tr key={r._key} className="border-b border-[var(--zemo-border)] last:border-0" data-testid={`review-receivable-${i}`}>
                    <td className="px-3 py-2 font-mono-num text-zinc-900">{r.external_id}</td>
                    <td className="px-3 py-2 text-zinc-700">{r.payer_name}</td>
                    <td className="px-3 py-2 text-zinc-600 text-xs">{backingTypeLabel(r.backing_type)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{formatBRL(parseDecimalBR(r.net_face_value))}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-zinc-600" data-testid={`review-receivable-pct-${i}`}>
                      {formatAdvancePercent(advancePercentFromValue(r.net_face_value, r.requested_advance_value)?.percent)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num font-semibold">{formatBRL(parseDecimalBR(r.requested_advance_value))}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-zinc-600">{r.due_date}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-50 border-t border-[var(--zemo-border)]">
                <tr>
                  {/* 7 colunas ao todo: o total continua alinhado SOB "A
                      antecipar", que agora é a 6ª (UI-5 moveu o % para
                      antes dela). */}
                  <td colSpan={5} className="px-3 py-2 font-medium text-zinc-700">Total a antecipar</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-zinc-900">{formatBRL(totalReceivables)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Assinantes do contrato (PJ): ordem = ordem de assinatura */}
      {cedenteIsPJ && selectedSigners.length > 0 && (
        <div className="space-y-2" data-testid="review-signers">
          <div className="zemo-overline">Assinantes do contrato ({selectedSigners.length})</div>
          <div className="border border-[var(--zemo-border)] rounded-md divide-y divide-[var(--zemo-border)]">
            {selectedSigners.map((rep, i) => (
              <div key={rep.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`review-signer-${i}`}>
                <span className="h-5 w-5 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-[11px] font-semibold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <span className="font-medium text-zinc-900">{rep.full_name}</span>
                  <span className="text-[12px] text-zinc-500 ml-2 font-mono-num">{maskCPF(rep.document_number)}</span>
                  <span className="text-[12px] text-zinc-500 ml-2">{rep.email}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Simulacao V2 na revisao — re-executada automaticamente ao entrar nesta etapa */}
      {simulating && (
        <div role="status" aria-live="polite" className="rounded-md p-3 border border-blue-200 bg-blue-50 text-[13px] text-blue-900 flex items-center gap-2" data-testid="review-simulating">
          <Loader2 className="h-4 w-4 animate-spin" />
          {isCedenteMode ? "Recalculando o valor final..." : "Recalculando simulacao V2 com os dados finais..."}
        </div>
      )}
      {!simulating && terminalError && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800" data-testid="review-terminal-error">
          {terminalError.message}
        </div>
      )}
      {!simulating && simulationError && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-2" data-testid="review-simulation-error">
          <div>{simulationError}</div>
          <button onClick={runSimulation} className="underline font-medium" data-testid="review-resimulate-btn">
            Tentar simular novamente
          </button>
        </div>
      )}
      {!simulating && !simulationError && !terminalError && simulationResult && (
        <div
          className={`rounded-md p-3 border text-[13px] ${(!isCedenteMode || liquidoVisivel)
                ? "border-green-200 bg-green-50 text-green-900"
                : "border-amber-200 bg-amber-50 text-amber-900"}`}
          data-testid="review-simulation"
        >
          {/* NUNCA fabricar "R$ 0,00": `formatBRL(parseFloat(x || 0))`
              transformava valor AUSENTE em "a Zemo te paga zero". Sem
              valor, o banner diz que não há valor — e o Confirmar trava. */}
          {isCedenteMode && !liquidoVisivel ? (
            <>Ainda não foi possível calcular o valor líquido desta operação — <strong>—</strong>. Tente recalcular antes de confirmar.</>
          ) : isCedenteMode ? (
            <>Valor liquido estimado: <strong>{formatBRL(parseFloat(oprLiquidValue))}</strong></>
          ) : (
            <>
              {/* Ramo do PARCEIRO: idêntico ao original (`|| 0`). O
                  endurecimento do BLOQ-3 é do money-path do cedente. */}
              Simulacao V2: valor liquido <strong>{formatBRL(parseFloat(oprLiquidValue || 0))}</strong>
              {oprDiscountValue && <> (desconto: {formatBRL(parseFloat(oprDiscountValue))})</>}
            </>
          )}
        </div>
      )}

      {/* Veredito REJEITADO na re-simulacao da revisao (item 16): cobre a
          corrida de quem avancou da etapa 3 antes de a simulacao responder.
          O Confirmar fica desabilitado; o create recusaria de toda forma. */}
      {!simulating && policyRejected && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-1" data-testid="review-policy-verdict-rejected">
          <div className="flex items-start gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{policyRejectText}</span>
          </div>
          {allowedRangeText && <div className="font-mono-num">{allowedRangeText}</div>}
          {cfg.showRates && (
            <div className="text-red-700">
              Volte e ajuste a taxa mensal ou os valores dos recebiveis —
              a operacao seria recusada pela politica de taxas do produto ao confirmar.
            </div>
          )}
        </div>
      )}

      <div className="rounded-md p-4 border text-[13px] border-green-200 bg-green-50 text-green-900" data-testid="submit-info-banner">
        {isCedenteMode ? (
          <>
            Ao confirmar, a operação será validada e criada com {validReceivables.length} recebíve(is)
            {cedenteIsPJ && <> e {selectedSigners.length} assinante(s) do contrato</>},
            incluindo os seus dados bancários (PIX), e seguirá para aprovação. Se a validação
            recusar, <strong>nada é criado</strong> e o motivo aparece aqui.
            O status é atualizado automaticamente.
          </>
        ) : (
          <>
            Ao confirmar, a operacao sera validada e criada <strong>primeiro na API V2</strong> com {validReceivables.length} recebive(is)
            {cedenteIsPJ && <> e {selectedSigners.length} assinante(s) do contrato</>},
            incluindo dados bancarios (PIX) e taxas — e so entao registrada no Portal. Se a V2 recusar
            (ex.: taxa fora da faixa permitida), <strong>nada e criado</strong> e o motivo aparece aqui.
            O status sera atualizado automaticamente por webhook.
          </>
        )}
      </div>
    </div>
  );
}
